import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  Users,
  UserCheck,
  Clock,
  MoreHorizontal,
  Trash2,
  Eye,
  Power,
  KeyRound,
  Shield,
  Copy,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface PortalAccess {
  id: string
  tenantId: string
  clientId: string
  email: string
  displayName: string | null
  role: string
  permissions: Record<string, unknown> | null
  lastLoginAt: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface PortalAccessWithPassword extends PortalAccess {
  temporaryPassword: string
}

interface PortalAccessStats {
  totalUsers: number
  activeUsers: number
  inactiveUsers: number
  byClient: Array<{ clientId: string; count: number }>
  byRole: Array<{ role: string; count: number }>
  recentLogins: number
}

interface PasswordResetResult {
  email: string
  temporaryPassword: string
}

// ============================================
// Helpers
// ============================================

const roleLabels: Record<string, string> = {
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
}

function roleBadge(role: string) {
  switch (role) {
    case 'admin':
      return <Badge className="bg-purple-500/10 text-purple-500 border-purple-500/20">Admin</Badge>
    case 'editor':
      return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Editor</Badge>
    case 'viewer':
      return <Badge variant="secondary">Viewer</Badge>
    default:
      return <Badge variant="secondary">{role}</Badge>
  }
}

function activeBadge(isActive: boolean) {
  if (isActive) {
    return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Active</Badge>
  }
  return <Badge className="bg-gray-500/10 text-gray-500 border-gray-500/20">Disabled</Badge>
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  return new Date(dateStr).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return formatDate(dateStr)
}

// ============================================
// Create Access Dialog
// ============================================

function CreateAccessDialog() {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [clientId, setClientId] = useState('')
  const [role, setRole] = useState('viewer')
  const [tempPassword, setTempPassword] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post<PortalAccessWithPassword>('/client-portal/access', data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['portal-access'] })
      queryClient.invalidateQueries({ queryKey: ['portal-access-stats'] })
      const password = response.data?.temporaryPassword
      if (password) {
        setTempPassword(password)
      } else {
        toast({ title: 'Access created', description: 'Portal access has been granted' })
        setOpen(false)
        resetForm()
      }
    },
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create portal access', variant: 'destructive' }),
  })

  function resetForm() {
    setEmail('')
    setDisplayName('')
    setClientId('')
    setRole('viewer')
    setTempPassword(null)
  }

  function handleClose(isOpen: boolean) {
    setOpen(isOpen)
    if (!isOpen) {
      resetForm()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" /> Grant Access
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Grant Portal Access</DialogTitle>
          <DialogDescription>
            Create portal access for a client contact. A temporary password will be generated.
          </DialogDescription>
        </DialogHeader>

        {tempPassword ? (
          <div className="space-y-4 py-4">
            <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
              <p className="text-sm font-medium">Portal access created successfully</p>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Temporary Password</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-background px-3 py-2 text-sm font-mono border">
                    {tempPassword}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(tempPassword)
                      toast({ title: 'Copied', description: 'Password copied to clipboard' })
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Share this password securely with the user. They will be able to change it on first login.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => handleClose(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contact@client.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>Client ID *</Label>
                <Input
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="Client UUID"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer - Read-only access</SelectItem>
                    <SelectItem value="editor">Editor - Can create and edit</SelectItem>
                    <SelectItem value="admin">Admin - Full portal access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button
                onClick={() =>
                  createMutation.mutate({
                    email,
                    displayName: displayName || undefined,
                    clientId,
                    role,
                  })
                }
                disabled={!email || !clientId || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Grant Access'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Access Detail Dialog
// ============================================

function AccessDetailDialog({
  access,
  onClose,
}: {
  access: PortalAccess
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [resetResult, setResetResult] = useState<PasswordResetResult | null>(null)

  const resetMutation = useMutation({
    mutationFn: () =>
      apiClient.post<PasswordResetResult>(`/client-portal/access/${access.id}/reset-password`),
    onSuccess: (response) => {
      if (response.data) {
        setResetResult(response.data)
      }
      toast({ title: 'Password reset', description: 'A new temporary password has been generated' })
    },
    onError: () =>
      toast({ title: 'Error', description: 'Failed to reset password', variant: 'destructive' }),
  })

  const toggleMutation = useMutation({
    mutationFn: () => apiClient.patch(`/client-portal/access/${access.id}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-access'] })
      queryClient.invalidateQueries({ queryKey: ['portal-access-stats'] })
      toast({
        title: access.isActive ? 'Access disabled' : 'Access enabled',
        description: `Portal access has been ${access.isActive ? 'disabled' : 'enabled'}`,
      })
      onClose()
    },
    onError: () =>
      toast({ title: 'Error', description: 'Failed to toggle access', variant: 'destructive' }),
  })

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{access.displayName || access.email}</DialogTitle>
          <DialogDescription>Portal access details</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="text-sm font-medium">{access.email}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Display Name</p>
              <p className="text-sm font-medium">{access.displayName || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Role</p>
              <div className="mt-0.5">{roleBadge(access.role)}</div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <div className="mt-0.5">{activeBadge(access.isActive)}</div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Client ID</p>
              <p className="text-sm font-medium font-mono text-xs">{access.clientId}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Last Login</p>
              <p className="text-sm font-medium">{formatDateTime(access.lastLoginAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Created</p>
              <p className="text-sm font-medium">{formatDate(access.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Updated</p>
              <p className="text-sm font-medium">{formatDate(access.updatedAt)}</p>
            </div>
          </div>

          {resetResult && (
            <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
              <p className="text-sm font-medium">New Temporary Password</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-background px-3 py-2 text-sm font-mono border">
                  {resetResult.temporaryPassword}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(resetResult.temporaryPassword)
                    toast({ title: 'Copied', description: 'Password copied to clipboard' })
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => toggleMutation.mutate()}
              disabled={toggleMutation.isPending}
            >
              <Power className="mr-2 h-4 w-4" />
              {access.isActive ? 'Disable Access' : 'Enable Access'}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => resetMutation.mutate()}
              disabled={resetMutation.isPending}
            >
              <KeyRound className="mr-2 h-4 w-4" />
              {resetMutation.isPending ? 'Resetting...' : 'Reset Password'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Main Page
// ============================================

export function ClientPortalPage() {
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [selectedAccess, setSelectedAccess] = useState<PortalAccess | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch portal access entries
  const { data: accessData, isLoading } = useQuery({
    queryKey: ['portal-access', activeFilter, roleFilter],
    queryFn: () =>
      apiClient.get<{ data: PortalAccess[]; meta: unknown }>('/client-portal/access', {
        ...(activeFilter !== 'all' && { isActive: activeFilter }),
        ...(roleFilter !== 'all' && { role: roleFilter }),
        limit: 100,
      }),
  })

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['portal-access-stats'],
    queryFn: () => apiClient.get<PortalAccessStats>('/client-portal/stats'),
  })

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: (id: string) => apiClient.patch(`/client-portal/access/${id}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-access'] })
      queryClient.invalidateQueries({ queryKey: ['portal-access-stats'] })
      toast({ title: 'Toggled', description: 'Portal access status updated' })
    },
    onError: () =>
      toast({ title: 'Error', description: 'Failed to toggle access', variant: 'destructive' }),
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/client-portal/access/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-access'] })
      queryClient.invalidateQueries({ queryKey: ['portal-access-stats'] })
      toast({ title: 'Revoked', description: 'Portal access has been revoked' })
    },
    onError: () =>
      toast({ title: 'Error', description: 'Failed to revoke access', variant: 'destructive' }),
  })

  const entries = accessData?.data?.data || accessData?.data || []
  const stats = statsData?.data || null

  const filtered = Array.isArray(entries)
    ? entries.filter(
        (e: PortalAccess) =>
          e.email.toLowerCase().includes(search.toLowerCase()) ||
          (e.displayName && e.displayName.toLowerCase().includes(search.toLowerCase()))
      )
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Client Portal</h1>
          <p className="text-muted-foreground">Manage client portal access and permissions</p>
        </div>
        <CreateAccessDialog />
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Total Users</p>
            </div>
            <div className="text-2xl font-bold mt-1">{stats?.totalUsers ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Portal accounts</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-green-500" />
              <p className="text-xs text-muted-foreground">Active Users</p>
            </div>
            <div className="text-2xl font-bold text-green-500 mt-1">
              {stats?.activeUsers ?? 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.inactiveUsers ?? 0} disabled
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              <p className="text-xs text-muted-foreground">Recent Logins</p>
            </div>
            <div className="text-2xl font-bold text-blue-500 mt-1">
              {stats?.recentLogins ?? 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Last 7 days</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-purple-500" />
              <p className="text-xs text-muted-foreground">Roles</p>
            </div>
            <div className="text-2xl font-bold text-purple-500 mt-1">
              {stats?.byRole?.length ?? 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {(stats?.byRole || [])
                .map((r) => `${r.count} ${roleLabels[r.role] || r.role}`)
                .join(', ') || 'No users'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by email or name..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Disabled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="editor">Editor</SelectItem>
            <SelectItem value="viewer">Viewer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-5 w-20" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No portal users found</p>
            <p className="text-sm text-muted-foreground">
              Grant access to a client contact to get started
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Display Name</TableHead>
                <TableHead>Client ID</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((entry: PortalAccess) => (
                <TableRow
                  key={entry.id}
                  className="cursor-pointer"
                  onClick={() => setSelectedAccess(entry)}
                >
                  <TableCell className="font-medium">{entry.email}</TableCell>
                  <TableCell>{entry.displayName || '-'}</TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-muted-foreground">
                      {entry.clientId.slice(0, 8)}...
                    </span>
                  </TableCell>
                  <TableCell>{roleBadge(entry.role)}</TableCell>
                  <TableCell>{activeBadge(entry.isActive)}</TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {timeAgo(entry.lastLoginAt)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedAccess(entry)
                          }}
                        >
                          <Eye className="mr-2 h-4 w-4" /> View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleMutation.mutate(entry.id)
                          }}
                        >
                          <Power className="mr-2 h-4 w-4" />
                          {entry.isActive ? 'Disable' : 'Enable'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteMutation.mutate(entry.id)
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Revoke Access
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Detail Dialog */}
      {selectedAccess && (
        <AccessDetailDialog
          access={selectedAccess}
          onClose={() => setSelectedAccess(null)}
        />
      )}
    </div>
  )
}
